import { type Goal } from '@/api/generated/model';
import { useGetProfile } from '@/api/generated/settings/settings.ts';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function calculateDaysRemainingStr(dueDateStr: string | null | undefined): number {
  if (!dueDateStr) return 0;
  return calculateDaysRemaining(new Date(dueDateStr));
}

export function calculateDaysRemaining(dueDate: Date): number {
  const today = new Date();
  return Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export const getGoalProgress = (goal: Goal): number => {
  return (goal.currentAmount / goal.targetAmount) * 100;
};

export const getGoalStatus = (goal: Goal): 'onTrack' | 'atRisk' | 'behind' => {
  const progress = getGoalProgress(goal);
  if (!goal.dueDate) return 'onTrack'; // If no due date, consider on track
  const dueDate = new Date(goal.dueDate);

  if (progress >= 100) return 'onTrack';
  const daysRemaining = calculateDaysRemaining(dueDate);
  if (daysRemaining <= 0) return 'behind';

  // Calculate if on track based on progress vs time elapsed
  const expectedProgress = 100 - (daysRemaining / 365) * 100;
  if (progress >= expectedProgress - 10) return 'onTrack';
  if (progress >= expectedProgress - 25) return 'atRisk';
  return 'behind';
};

export const formatDate = (date: string): string => {
  const inputDate = new Date(date);
  const today = new Date();

  // Check if the date is today
  const isToday = inputDate.toDateString() === today.toDateString();
  const currentYear = inputDate.getFullYear() === today.getFullYear();

  if (isToday) {
    // For today, show only time in 24-hour format
    return inputDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } else if (currentYear) {
    return inputDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } else {
    return inputDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
};

/**
 * Returns the color associated with an account type
 * @param type The account type
 * @returns The color in hex format
 */
export const getAccountTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    bank: '#1E5591',
    investment: '#2E7D32',
    'real-estate': '#ED6C02',
    crypto: '#9C27B0',
    alternative: '#1976D2',
    liability: '#D32F2F',
  };
  return colors[type] || '#757575';
};

// Currency symbols mapping
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: '$',
  CHF: 'CHF',
  AUD: 'A$',
  CNY: '¥',
  RUB: '₽',
};

export const SUPPORTED_CURRENCIES = [
  'GBP',
  'USD',
  'EUR',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'CNY',
  'RUB',
] as const;

// Default currency fallback
export const DEFAULT_CURRENCY = 'USD';

// Hook to get user's default currency
export const useDefaultCurrency = () => {
  const { data: settings } = useGetProfile();
  return settings?.preferences?.currency || DEFAULT_CURRENCY;
};

// Format amount with currency
export const formatCurrency = (amount: number, currency: string = DEFAULT_CURRENCY) => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;

  // Handle negative amounts
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);

  // Format the number
  const formattedAmount = absAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Return with appropriate sign and symbol
  return `${isNegative ? '-' : ''}${symbol}${formattedAmount}`;
};

// Get currency symbol
export const getCurrencySymbol = (currency: string = DEFAULT_CURRENCY) => {
  return CURRENCY_SYMBOLS[currency] || currency;
};
/**
 * Returns a relative time string (e.g., "2d ago", "3h ago")
 * @param dateString The date string to convert
 * @returns A relative time string
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInDays > 0) {
    return `${diffInDays}d ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours}h ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes}m ago`;
  } else {
    return 'Just now';
  }
};

/**
 * Returns a formatted date string (dd/mm/yyyy)
 * @param dateString The date string to format
 * @returns A formatted date string
 */
export const getFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};
